import { type ReactElement } from 'react';
import { type GetServerSideProps } from 'next';
import dynamic from 'next/dynamic';
import { ErrorBoundary, captureException, type FallbackRender } from '@sentry/nextjs';
import useSWR from 'swr';

import { useTheme } from '@mui/material/styles';
import { Box, useMediaQuery } from '@mui/material';

import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';

import { useErrorHandler } from '../../../utils/errors';

import Preloader from '../../../components/preloader';
import FallbackComponent from '../../../components/fallback-component';
import SeoTags from '../../../components/seo-tags';
import { withStrapi } from '../../../utils/api/Strapi';

const SignUpForm = dynamic(() => import('../../../components/sign-up-form'), {
  loading: () => <Preloader />,
});

export default function SignUpPage({ errorMessage }: PageProps): ReactElement {
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up('md'));
  useErrorHandler(errorMessage);
  const signupPage = useSWR<StaticPageProps>(!errorMessage ? '/api/v1/sign-up-page?populate=*' : null);
  const { data, isLoading, isValidating } = signupPage;

  if (isLoading || isValidating) {
    return <Preloader />;
  }

  if (!data) {
    return <Preloader />;
  }

  const fallback: FallbackRender = ({ error, resetError }) => (
    <FallbackComponent error={error as Error} resetError={resetError}>
      <GoogleReCaptchaProvider reCaptchaKey="6LcjXi0aAAAAAItAfc8jOW0lCJsgEK6BnHyXekK3">
        <SignUpForm content={data.content} />
      </GoogleReCaptchaProvider>
    </FallbackComponent>
  );

  return (
    <>
      <SeoTags {...data.seo} />
      <Box component="main" maxWidth="lg" sx={{ placeContent: matches ? 'center' : undefined, mx: 'auto' }}>
        <ErrorBoundary
          fallback={fallback}
          onError={(error) => captureException(error)}
        >
          <GoogleReCaptchaProvider reCaptchaKey="6LcjXi0aAAAAAItAfc8jOW0lCJsgEK6BnHyXekK3">
            <SignUpForm content={data.content} />
          </GoogleReCaptchaProvider>
        </ErrorBoundary>
      </Box>
    </>
  );
}

export const getServerSideProps: GetServerSideProps<GetServerSidePropsResult<PageProps>> = withStrapi(
  async ({ api }) => {
    const { user } = api;

    if (user) {
      return { redirect: { destination: '/customer-area', permanent: false } };
    }

    const { data } = await api.request<{ data: StaticPageProps }>('GET', 'sign-up-page?populate=*');

    return {
      props: {
        fallback: {
          '/api/v1/auth/me': user,
          '/api/v1/sign-up-page?populate=*': data,
        },
      },
    };
  },
);
